import React, {
  ElementType,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuItemProps,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import map from 'lodash/map';

import { Density } from '@sbv-clients/util-misc';

import { withTitle } from '../../hoc/with-title';
import { Box } from '../box/box';
import { CHECKBOX_CHECK_PATH } from '../checkbox/check-path';
import {
  useWindowedTooltipZIndex,
  withWindowedMenuProps,
} from '../dialog/windowed-tooltip-zindex-context';
import { Icon, IconName } from '../icon';
import { GridEditChevron } from './grid-edit-chevron';
import { useScaledMenuProps } from './use-scaled-menu-props';

export type SelectOption<T> = {
  id: string | number;
  label?: ReactNode;
  value?: number | string;
  disabled?: boolean;
  inActive?: boolean;
  meta?: any;
};

type SelectProps<T> = Omit<
  MuiSelectProps<T>,
  'onChange' | 'value' | 'renderValue'
> & {
  firstOptionAsDefault?: boolean;
  isLoading?: boolean;
  options?: SelectOption<T>[];
  value?: T;
  helperText?: ReactNode;
  MenuItem?: FC<MenuItemProps>;
  sx?: MuiSelectProps<T>['sx'];
  iconName?: IconName;
  onChange?: (event: SelectChangeEvent<T>, child: ReactNode, meta: any) => void;
  hideStartAdornment?: boolean;
  notSelectedLabel?: string;
  applyDataGridInputStyles?: boolean;
  defaultOpen?: boolean;
  renderValue?: (selected: unknown) => any;
  placeholder?: string;
  density?: Density;
  clearable?: boolean;
  onClear?: () => void;
};

const gridEditMenuPaperSx = (theme: Theme) => ({
  mt: '6px',
  borderRadius: '12px',
  boxShadow: theme.custom?.columnsPopoverShadow,
  '& .MuiMenu-list': { padding: '6px', minWidth: 160 },
  '& .MuiMenuItem-root.MuiButtonBase-root': {
    height: 'auto',
    minHeight: 34,
    margin: '1px 0',
    padding: '0 10px',
    gap: '10px',
    justifyContent: 'space-between',
    borderRadius: '8px',
    fontSize: '13.5px',
    color: theme.custom?.shellMenuItemText,
    background: 'none',
    '&:hover': { background: theme.custom?.shellMenuItemHoverBg },
    '&.Mui-selected, &.Mui-selected:hover': {
      fontWeight: 600,
      color: theme.custom?.brandNavy,
      background: theme.custom?.shellRoleBg,
    },
  },
  '& .grid-edit-tick': {
    flexShrink: 0,
    width: 16,
    height: 16,
    fill: theme.colors.primary.main,
  },
});

const dataGridInputStyles = (applyDataGridInputStyles?: boolean) => {
  if (!applyDataGridInputStyles) {
    return {};
  }
  return {
    borderRadius: 0,
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 0,
      fontSize: '13px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderRadius: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderRadius: 'none',
    },
  } as any;
};

const SelectNoTitle = <T,>({
  firstOptionAsDefault,
  isLoading,
  required,
  value,
  size,
  name,
  onChange,
  onBlur,
  label,
  helperText,
  error,
  fullWidth,
  disabled,
  options = [],
  className,
  MenuProps,
  onClose,
  onOpen,
  onClick,
  open,
  sx,
  iconName = 'KeyboardArrowDown',
  hideStartAdornment,
  notSelectedLabel = 'Not Selected',
  applyDataGridInputStyles,
  defaultOpen,
  renderValue,
  density = 'compact',
  clearable,
  onClear,
}: SelectProps<T>) => {
  const windowedConfig = useWindowedTooltipZIndex();
  const {
    anchorRef,
    measure,
    menuProps: scaledMenuProps,
  } = useScaledMenuProps(MenuProps, open);
  const theme = useTheme();
  const selected = value !== '' && value !== null && value !== undefined;
  const [isHovered, setIsHovered] = useState(false);

  const startAdornment = useMemo(() => {
    if (hideStartAdornment) return null;
    if (isLoading) {
      return (
        <InputAdornment position="start">
          <CircularProgress size={14} />
        </InputAdornment>
      );
    }
    if (selected) return null;
    return (
      <InputAdornment
        position="start"
        sx={{ width: '0px !important', color: theme.text.placeholder }}
      >
        <span style={{ fontSize: 'small' }}>{notSelectedLabel}</span>
      </InputAdornment>
    );
  }, [isLoading, selected, hideStartAdornment, notSelectedLabel, theme]);
  const SelectIcon: ElementType = (iconProps: any) => (
    <Icon
      name={iconName}
      sx={{
        alignItems: 'end',
        justifyContent: 'end',
        display: 'flex',
        width: 'auto !important',
      }}
      {...iconProps}
    />
  );
  useEffect(() => {
    if (!firstOptionAsDefault || selected || !options.length) {
      return;
    }

    const firstOption = options[0];

    if (!firstOption?.value) return;

    const fakeEvent = {
      target: { value: firstOption.value },
    } as SelectChangeEvent<T>;

    onChange?.(fakeEvent, firstOption.label, firstOption.meta);
  }, [firstOptionAsDefault, selected, options, onChange]);
  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      required={required}
      error={error}
      disabled={disabled || isLoading}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <MuiSelect
        defaultOpen={defaultOpen}
        disabled={disabled || isLoading}
        onClick={onClick}
        ref={anchorRef}
        onOpen={(event) => {
          measure();
          onOpen?.(event);
        }}
        name={name}
        error={error}
        open={open}
        value={!selected ? '' : value ?? ('' as any)}
        onChange={(event, child) => {
          const meta = options?.find(
            (item) => item.value === event.target.value
          )?.meta;
          onChange?.(event as SelectChangeEvent<T>, child, meta);
        }}
        onBlur={onBlur}
        MenuProps={withWindowedMenuProps(
          windowedConfig,
          applyDataGridInputStyles
            ? {
                ...scaledMenuProps,
                slotProps: {
                  ...scaledMenuProps?.slotProps,
                  paper: {
                    ...(scaledMenuProps?.slotProps?.paper as object),
                    sx: gridEditMenuPaperSx(theme),
                  },
                },
              }
            : scaledMenuProps
        )}
        onClose={onClose}
        sx={() => ({
          ...sx,
          ...dataGridInputStyles(applyDataGridInputStyles),
          height: density === 'comfortable' ? '42px' : '32px',
        })}
        startAdornment={startAdornment}
        endAdornment={
          clearable && selected ? (
            <InputAdornment
              position="end"
              sx={{ mr: 3, visibility: isHovered ? 'visible' : 'hidden' }}
            >
              <IconButton
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear?.();
                }}
              >
                <Icon name="Close" sx={{ fontSize: 8, color: 'grey.500' }} />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
        IconComponent={applyDataGridInputStyles ? GridEditChevron : SelectIcon}
        renderValue={renderValue}
      >
        {map(options, (item) => {
          const inactiveStyles = item.inActive ? { opacity: 0.5 } : {};
          return (
            <MenuItem
              key={item.id}
              value={item.value}
              disabled={item.disabled}
              style={
                applyDataGridInputStyles
                  ? inactiveStyles
                  : { fontSize: 'smaller', ...inactiveStyles }
              }
            >
              {item.label}
              {applyDataGridInputStyles && item.value === value && (
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  aria-hidden
                  className="grid-edit-tick"
                >
                  <path d={CHECKBOX_CHECK_PATH} />
                </Box>
              )}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {error && <FormHelperText error>{helperText}</FormHelperText>}
      {!error && disabled && helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

const Select = withTitle(SelectNoTitle);
export type { SelectProps, SelectChangeEvent };
export { Select, MuiSelect };
export default withTitle(Select);
